import "@/sim/sim.css";
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Radar as RadarIcon, ShieldAlert, Loader2, ArrowLeft } from "lucide-react";
import { simMe, simExchangeSession, simLogout, simGoogleLogin } from "@/sim/lib";

const SimAuthContext = createContext(null);
export const useSimAuth = () => useContext(SimAuthContext) || {};

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.6C29.7 34.6 27 35.5 24 35.5c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6.6 5.6C41.4 36.3 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

function SimAuthScreen({ reason }) {
  const navigate = useNavigate();
  const blocked = reason === "blocked";
  const forbidden = reason === "forbidden";
  return (
    <div className="sim-root sim-grid-bg flex items-center justify-center min-h-screen px-5">
      <div className="sim-card-elevated rounded-2xl p-8 max-w-md w-full text-center" data-testid="sim-login">
        <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center mx-auto mb-5">
          {blocked || forbidden ? <ShieldAlert className="w-6 h-6 text-[#F59E0B]" /> : <RadarIcon className="w-6 h-6 text-[#00F0FF]" />}
        </div>
        <h1 className="sim-head text-2xl font-extrabold text-[#F5F5F5]">
          {blocked ? "Access revoked" : forbidden ? "Admins only" : "sim.roas-ai"}
        </h1>
        <p className="text-[#D4D4D8] text-sm mt-2 mb-6">
          {blocked
            ? "An admin has removed access for this account. Contact the simulator owner if you think this is a mistake."
            : forbidden
            ? "This page is restricted to simulator admins."
            : "Private synthetic-audience simulator. Sign in with your Google account to continue."}
        </p>

        {forbidden ? (
          <button data-testid="sim-back-btn" onClick={() => navigate("/sim")}
            className="w-full h-11 rounded-lg bg-white/5 border border-[#27272A] text-[#F5F5F5] text-sm font-semibold flex items-center justify-center gap-2 hover:border-[#3f3f46] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to simulator
          </button>
        ) : (
          <button data-testid="sim-google-btn" onClick={simGoogleLogin}
            className="w-full h-11 rounded-lg bg-white text-[#111] text-sm font-bold flex items-center justify-center gap-2.5 hover:bg-[#f1f1f1] transition-colors">
            <GoogleIcon /> Continue with Google
          </button>
        )}

        {blocked && (
          <button onClick={simGoogleLogin} className="mt-3 sim-mono text-[11px] text-[#C2C5CE] hover:text-[#F5F5F5] transition-colors">
            Try a different Google account
          </button>
        )}
      </div>
    </div>
  );
}

function SimLoader() {
  return (
    <div className="sim-root flex items-center justify-center min-h-screen">
      <div className="text-center">
        <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin mx-auto mb-3" />
        <p className="sim-mono text-[11px] text-[#C2C5CE]">AUTHENTICATING…</p>
      </div>
    </div>
  );
}

export function SimGate({ children, requireAdmin = false, publicFallback = null }) {
  const location = useLocation();
  const [status, setStatus] = useState("checking"); // checking | authed | login | blocked | forbidden
  const [user, setUser] = useState(null);
  const processed = useRef(false);

  const logout = useCallback(async () => {
    await simLogout();
    setUser(null);
    setStatus("login");
  }, []);

  useEffect(() => {
    const run = async () => {
      const hash = window.location.hash || "";
      const hasSession = hash.includes("session_id=");
      if (hasSession) {
        if (processed.current) return;
        processed.current = true;
        const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
        try {
          const u = await simExchangeSession(sid);
          // clear the hash + sim_auth query
          window.history.replaceState(null, "", requireAdmin ? "/sim/admin" : "/sim");
          setUser(u);
          setStatus(requireAdmin && !u.is_admin ? "forbidden" : "authed");
        } catch (e) {
          window.history.replaceState(null, "", requireAdmin ? "/sim/admin" : "/sim");
          setStatus(e.code === "blocked" ? "blocked" : "login");
        }
        return;
      }
      const u = await simMe();
      if (!u) { setStatus("login"); return; }
      setUser(u);
      setStatus(requireAdmin && !u.is_admin ? "forbidden" : "authed");
    };
    run();
  }, [location.hash, requireAdmin]);

  if (status === "checking") return <SimLoader />;
  if (status === "authed") {
    return (
      <SimAuthContext.Provider value={{ user, logout }}>
        {children}
      </SimAuthContext.Provider>
    );
  }
  // Logged-out visitors on a page with a public marketing fallback (the /sim landing).
  if (status === "login" && publicFallback) return publicFallback;
  return <SimAuthScreen reason={status} />;
}
